import { Link } from "react-router-dom";
import { Columns, Eye, GearSix, ListBullets, ShieldCheck, SignOut, UserCircle } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { isAdmin, isPasswordAdmin, useUser } from "@/lib/user";
import { ThemeToggle } from "@/components/observer/ThemeToggle";
import { InstallMenuItem } from "@/components/observer/InstallPrompt";

const linkCls = "flex items-center gap-2 px-2 sm:px-3 py-2 text-sm text-[var(--o-muted)] hover:text-[var(--o-fg)] hover:bg-[var(--o-surface)] transition-colors";

export const AppHeader = ({ right }) => {
  const user = useUser();
  const admin = isPasswordAdmin(user);
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    window.location.assign(admin ? "/admin/login" : "/login");
  };
  const initials = admin ? "A" : (user?.name || "?").split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <header className="relative z-10 h-16 flex items-center justify-between gap-2 px-4 sm:px-10 border-b border-[var(--o-border)] bg-[var(--o-bg)]">
      <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 hover:opacity-80 transition-opacity" data-testid="header-home-link">
        <Eye size={20} weight="bold" />
        <span className="font-display text-base sm:text-lg font-extrabold tracking-tight whitespace-nowrap" data-testid="app-brand">GoNav.Tech - Observer</span>
        <span className="hidden sm:inline font-mono text-[10px] text-[var(--o-muted3)] border border-[var(--o-border)] px-1.5 py-0.5">v1</span>
      </Link>
      <div className="flex items-center gap-1">
        {right}
        {!admin && (
          <Link to="/meetings" className={linkCls} data-testid="header-meetings-link">
            <ListBullets size={16} /> <span className="hidden sm:inline">Meetings</span>
          </Link>
        )}
        {!admin && (
          <Link to="/compare" className={linkCls} data-testid="header-compare-link">
            <Columns size={16} /> <span className="hidden sm:inline">Compare</span>
          </Link>
        )}
        {isAdmin(user) && (
          <Link to="/admin" className={linkCls} data-testid="header-admin-link">
            <GearSix size={16} /> <span className="hidden sm:inline">Admin</span>
          </Link>
        )}
        <ThemeToggle className="sm:mx-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 h-8 w-8 rounded-full border border-[var(--o-border-strong)] font-mono text-[11px] font-semibold hover:bg-[var(--o-surface)] transition-colors" title={user ? `${user.name} · ${user.email}` : "Account"} data-testid="header-user-menu">
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-none border-[var(--o-border)] bg-[var(--o-bg2)] text-[var(--o-fg)]">
            <DropdownMenuLabel className="font-normal">
              <div className="text-sm font-semibold truncate">{admin ? "Admin" : user?.name}</div>
              <div className="text-xs text-[var(--o-muted2)] truncate">{admin ? "signed in with the admin password" : user?.email}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--o-muted3)] mt-1">{user?.role}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[var(--o-border)]" />
            {!admin && (
              <DropdownMenuItem asChild className="cursor-pointer rounded-none">
                <Link to="/account" data-testid="header-account-link"><UserCircle size={15} className="mr-2" /> Account</Link>
              </DropdownMenuItem>
            )}
            <InstallMenuItem />
            <DropdownMenuItem asChild className="cursor-pointer rounded-none">
              <Link to="/privacy" data-testid="header-privacy-link"><ShieldCheck size={15} className="mr-2" /> Privacy</Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={logout} className="cursor-pointer rounded-none" data-testid="header-logout-button">
              <SignOut size={15} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
